// AppWithSSO.js - Main App with SSO Support (Hybrid Login)
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import Login from "./Login";
import AgentAssist from "./AgentAssist";
import TicketDetailsPage from "./TicketDetailsPage";
import Analytics from './Analytics';
import UserAnalyticsFinal from './useranalyticsfinal';
import UserAnalyticsTest from './UserAnalyticsTest';
import useIdleTimer from './hooks/useIdleTimer';
import useSessionTracking from './hooks/useSessionTracking';
import { API_SCOPE } from './authConfig';

function AppContent() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();
  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authenticationError, setAuthenticationError] = useState('');
  const activeAccountHomeId = accounts?.[0]?.homeAccountId;

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    let isCurrent = true;

    const restoreSession = async () => {
      try {
        const existingResponse = await fetch('/api/auth/me');
        if (!isCurrent) return;
        if (existingResponse.ok) {
          const result = await existingResponse.json();
          if (isCurrent) setAuthenticatedUser(result.user);
          return;
        }
        if (existingResponse.status !== 401) {
          throw new Error(existingResponse.status === 403
            ? 'Continue with SSO. If sign-in is still blocked, contact your administrator to check the allowed application URL.'
            : 'Unable to check your application session. Please try again.');
        }

        const activeAccount = instance.getAllAccounts().find((account) => account.homeAccountId === activeAccountHomeId);
        if (isAuthenticated && activeAccount) {
          let tokenResult;
          try {
            tokenResult = await instance.acquireTokenSilent({
              account: activeAccount,
              scopes: [API_SCOPE]
            });
          } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
              if (isCurrent) setAuthenticationError('Continue with SSO to finish signing in.');
              return;
            }
            throw error;
          }
          if (!isCurrent) return;
          const exchangeResponse = await fetch('/api/auth/sso', {
            method: 'POST',
            headers: { Authorization: `Bearer ${tokenResult.accessToken}` }
          });
          if (!exchangeResponse.ok) {
            const result = await exchangeResponse.json();
            throw new Error(result.error === 'Origin is not allowed'
              ? 'Continue with SSO. If sign-in is still blocked, contact your administrator to check the allowed application URL.'
              : 'SSO could not establish an application session. Contact your administrator to check API consent and server settings.');
          }
          const result = await exchangeResponse.json();
          if (isCurrent) setAuthenticatedUser(result.user);
          return;
        }
        if (isCurrent) setAuthenticatedUser(null);
      } catch (error) {
        console.error('Unable to restore application session:', error.message);
        if (isCurrent) {
          setAuthenticatedUser(null);
          setAuthenticationError(error.message || 'Continue with SSO.');
        }
      } finally {
        if (isCurrent) setAuthLoading(false);
      }
    };

    restoreSession();
    return () => { isCurrent = false; };
  }, [isAuthenticated, activeAccountHomeId, instance, inProgress]);

  const userEmail = authenticatedUser?.email || null;
  const userAuthenticated = Boolean(authenticatedUser);

  // Track user session and screen time - get endSession function
  const { endSession } = useSessionTracking(userAuthenticated ? userEmail : null);

  // Handle logout for SSO
  const handleLogout = async () => {
    if (endSession) {
      await endSession();
    }
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch (error) {
      console.warn('Could not revoke the server session during logout:', error.message);
    }
    setAuthenticatedUser(null);
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  };
  
  const handleLogin = async (email, password) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        return result.error || 'Invalid email or password';
      }
      setAuthenticationError('');
      setAuthenticatedUser(result.user);
      return null;
    } catch (error) {
      return error.message || 'Email/password sign-in failed';
    }
  };

  const handleEmailLogout = async () => {
    if (endSession) {
      await endSession();
    }
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch (error) {
      console.warn('Could not revoke the server session during logout:', error.message);
    }
    setAuthenticatedUser(null);
  };

  const currentLogoutHandler = authenticatedUser?.authType === 'entra' ? handleLogout : handleEmailLogout;

  // Auto-logout after 15 minutes of inactivity
  useIdleTimer(() => {
    if (userAuthenticated) {
      alert('You have been logged out due to inactivity.');
      currentLogoutHandler();
    }
  }, 15 * 60 * 1000); // 15 minutes in milliseconds

  const userName = authenticatedUser?.name || userEmail?.split('@')[0] || 'User';
  const canViewUserAnalytics = userAuthenticated;

  if (authLoading) {
    return <div role="status" aria-live="polite">Checking authentication...</div>;
  }

  return (
    <>
      <Routes>
        {/* Login Route - Hybrid login page with both SSO and email/password */}
        <Route 
          path="/" 
          element={
            userAuthenticated ? (
              <Navigate to="/AgentAssist" replace />
            ) : (
              <Login onLogin={handleLogin} authenticationError={authenticationError} />
            )
          } 
        />

        {/* Protected Routes */}
        <Route 
          path="/AgentAssist" 
          element={
            userAuthenticated ? (
              <AgentAssist onLogout={currentLogoutHandler} userName={userName} userEmail={userEmail} canViewUserAnalytics={canViewUserAnalytics} />
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        
        <Route 
          path="/process/:ticketId" 
          element={
            userAuthenticated ? (
              <TicketDetailsPage userName={userName} userEmail={userEmail} onLogout={currentLogoutHandler} canViewUserAnalytics={canViewUserAnalytics} />
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        
        <Route 
          path="/analytics" 
          element={
            userAuthenticated ? (
              <Analytics userName={userName} userEmail={userEmail} onLogout={currentLogoutHandler} canViewUserAnalytics={canViewUserAnalytics} />
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        
        <Route 
          path="/screen-time-analytics" 
          element={
            userAuthenticated && canViewUserAnalytics ? (
              <UserAnalyticsFinal userName={userName} userEmail={userEmail} onLogout={currentLogoutHandler} canViewUserAnalytics={canViewUserAnalytics} />
            ) : (
              <Navigate to="/AgentAssist" replace />
            )
          } 
        />
        
        <Route 
          path="/user-analytics" 
          element={
            userAuthenticated && canViewUserAnalytics ? (
              <UserAnalyticsFinal userName={userName} userEmail={userEmail} onLogout={currentLogoutHandler} canViewUserAnalytics={canViewUserAnalytics} />
            ) : (
              <Navigate to="/AgentAssist" replace />
            )
          } 
        />

        <Route 
          path="/user-analytics-test" 
          element={
            userAuthenticated && canViewUserAnalytics ? (
              <UserAnalyticsTest userName={userName} userEmail={userEmail} onLogout={currentLogoutHandler} canViewUserAnalytics={canViewUserAnalytics} />
            ) : (
              <Navigate to="/AgentAssist" replace />
            )
          } 
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
